import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Zap, FileDown, Copy, Pencil, Loader2, AlertTriangle, MapPin, GitCompare, FileText, ArrowUpRight, Maximize2, Minimize2, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEFAULTS, EVENT_TYPES, KINDS, STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import EventMap, { NoEntryMini } from "@/components/map/EventMap";
import { DrawToolbar } from "@/components/map/DrawToolbar";
import { MapLayersPanel } from "@/components/map/MapLayersPanel";
import { STAFF_GROUPS, StaffMini } from "@/components/map/StaffMarkers";
import { DetourPanel, StaffPanel } from "@/components/map/PickPanels";
import { TimeBar, staffAt } from "@/components/map/TimeBar";
import { MeasurePanel, BoxSavePanel, GroupPlacePanel } from "@/components/map/MapTools";
import { MeasureButton } from "@/components/map/DrawToolbar";
import { pathM } from "@/lib/geo";
import { inBox, offsetOf, applyOffset, simplify } from "@/lib/geo";
import { SIGNS, SignMini } from "@/components/map/SignObjects";
import { ElementForm } from "@/components/ElementForm";
import { ShiftsBriefing, defaultShifts, totalsFromShifts } from "@/components/ShiftsBriefing";
import { AssignPanel } from "@/components/plan/AssignPanel";
import { buildUnits, staffPointsFrom, unitPosts, toMin, hm as hmOf, isMine } from "@/lib/staff";
import { EventInfoFields, DuplicateDialog } from "@/components/EventDialogs";
import { StaffGantt } from "@/components/plan/StaffGantt";
import { AiReport } from "@/components/plan/AiReport";
import { PlanSummary, DetoursList, SignageTable } from "@/components/plan/PlanParts";
import { TemplatesPanel, saveTemplate } from "@/components/TemplatesPanel";

const roadName = async ([lat, lon]) => {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=17&accept-language=es`);
    const j = await r.json();
    return j.address?.road || j.address?.pedestrian || "";
  } catch { return ""; }
};

const ElementList = ({ elements, selectedId, onSelect, timings }) => (
  <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1" data-testid="elements-list">
    {elements.length === 0 && <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-white/10 rounded-lg">Use las herramientas del mapa para marcar recorrido, cortes y zonas.</div>}
    {elements.map((e) => (
      <button key={e.id} onClick={() => onSelect(e.id)} data-testid={`element-item-${e.id}`}
        className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${selectedId === e.id ? "border-sky-500/50 bg-sky-500/10" : "border-white/5 bg-[#0b1329] hover:bg-[#172447]"}`}>
        {e.auto_generated ? <NoEntryMini /> : e.kind === "sign" ? <SignMini type={e.sign_type} rot={e.rotation} size={14} /> : <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: KINDS[e.kind].color }} />}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-xs text-slate-200"><span className="truncate">{e.name || KINDS[e.kind].short}</span>
            {e.auto_generated && <span className={`shrink-0 text-[9px] px-1.5 rounded border ${e.locked ? "border-rose-400/60 text-rose-200" : "border-rose-400/30 text-rose-300/80"}`} data-testid={`element-auto-badge-${e.id}`}>{e.locked ? "Aceptado" : "Propuesta"}</span>}
          </span>
          <span className="block font-mono text-[10px] text-slate-500">{e.kind === "sign" ? `${SIGNS[e.sign_type]?.code || "Objeto"}${SIGNS[e.sign_type]?.qty ? ` x${e.qty || 1}` : ""}` : KINDS[e.kind].short}{timings?.[e.id] ? ` · ${timings[e.id].start_hm}–${timings[e.id].end_hm}` : ""}</span>
        </span>
      </button>
    ))}
  </div>
);

export default function EventEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit } = useAuth();
  const [ev, setEv] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [mode, setMode] = useState(null);
  const [assignKey, setAssignKey] = useState(null);
  const [focus, setFocus] = useState(null);
  const [draft, setDraft] = useState([]);
  const [sel, setSel] = useState(null);
  const [fit, setFit] = useState(0);
  const [busy, setBusy] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [dup, setDup] = useState(null);
  const [planTab, setPlanTab] = useState("summary");
  const [sideTab, setSideTab] = useState("elements");
  const [briefingNew, setBriefingNew] = useState(false);
  const [oneWays, setOneWays] = useState(false);
  const [base, setBase] = useState(() => localStorage.getItem("map_base") || "dark");
  const [hidden, setHidden] = useState([]);
  const [full, setFull] = useState(false);
  const [measure, setMeasure] = useState([]);
  const [box, setBox] = useState([]);
  const [group, setGroup] = useState(null);
  const [pick, setPick] = useState(null);
  const [at, setAt] = useState(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { localStorage.setItem("map_base", base); }, [base]);
  const delRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "Delete" || e.key === "Supr") && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) delRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);
  const [oneWayStatus, setOneWayStatus] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => {
      const d = r.data;
      if (!d.shifts?.length) { d.shifts = defaultShifts(d); setBriefingNew(true); setSideTab("briefing"); }
      setEv(d); setFit((f) => f + 1);
    }).catch((e) => { toast.error(errMsg(e)); nav("/"); });
  }, [id, nav]);

  const update = useCallback((patch) => { setEv((p) => ({ ...p, ...patch })); setDirty(true); }, []);
  const setElements = (fn) => update({ elements: fn(ev.elements) });
  const buildGroup = (g) => g.tpl.items.map((it, i) => ({
    id: `${g.gid}-${i}`, kind: "sign", group_id: g.gid, sign_type: it.sign_type, qty: it.qty || 1,
    rotation: ((it.rotation || 0) + g.rot) % 360, start_time: it.start_time || "", end_time: it.end_time || "",
    name: `${g.tpl.name} · ${SIGNS[it.sign_type]?.label || "Objeto"}`, coords: [applyOffset(g.anchor, it.offset, g.rot)],
  }));
  const placeGroup = (g) => {
    setGroup(g);
    if (g.anchor) update({ elements: [...ev.elements.filter((e) => e.group_id !== g.gid), ...buildGroup(g)] });
  };
  const cancelGroup = () => {
    if (group) update({ elements: ev.elements.filter((e) => e.group_id !== group.gid) });
    setGroup(null); setMode(null);
  };
  const saveBoxTemplate = async (name) => {
    const items = ev.elements.filter((e) => e.kind === "sign" && e.sign_type !== "valla_linea" && inBox(e.coords[0], box[0], box[1]));
    const center = [(box[0][0] + box[1][0]) / 2, (box[0][1] + box[1][1]) / 2];
    try {
      await api.post("/templates", {
        kind: "sign_group", name, coords: [center],
        items: items.map((e) => ({ sign_type: e.sign_type, qty: e.qty || 1, rotation: e.rotation || 0, start_time: e.start_time || "", end_time: e.end_time || "", offset: offsetOf(center, e.coords[0]).map((v) => +v.toFixed(2)) })),
      });
      window.dispatchEvent(new Event("templates-changed"));
      toast.success(`Plantilla "${name}" guardada con ${items.length} objeto(s)`);
      setBox([]); setMode(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addFromTemplate = (t) => {
    if (t.kind === "sign_group") {
      setMode("place_group"); setDraft([]);
      setGroup({ tpl: t, gid: crypto.randomUUID(), anchor: null, rot: 0 });
      toast.info("Pulse en el mapa el cruce donde colocar la plantilla", { duration: 2500 });
      return;
    }
    const { id: _tid, created_by, created_at, ...props } = t;
    const existingRoute = t.kind === "route" && ev.elements.find((e) => e.kind === "route");
    const el = { ...props, id: existingRoute ? existingRoute.id : crypto.randomUUID() };
    if (t.kind === "no_parking") { el.start_time = ev.start_time; el.end_time = ev.end_time; }
    if (t.kind === "route") el.start_time = ev.start_time;
    setElements((els) => (existingRoute ? els.map((e) => (e.id === existingRoute.id ? el : e)) : [...els, el]));
    setSel(el.id);
    setSideTab("elements");
    toast.success(`"${t.name}" añadido al evento`);
  };



  const addElement = async (kind, coords, presetName, extra = {}) => {
    const existingRoute = kind === "route" && ev.elements.find((e) => e.kind === "route");
    if (existingRoute) {
      setElements((els) => els.map((e) => (e.id === existingRoute.id ? { ...e, coords } : e)));
      setSel(existingRoute.id);
      toast.info("Recorrido actualizado");
      return;
    }
    const newId = crypto.randomUUID();
    const n = ev.elements.filter((e) => e.kind === kind).length + 1;
    const el = { id: newId, kind, coords, name: presetName || `${KINDS[kind].short} ${n}`, start_time: kind === "route" ? ev.start_time : "", end_time: "", ...DEFAULTS[kind], ...extra };
    if (kind === "no_parking") { el.start_time = ev.start_time; el.end_time = ev.end_time; }
    setEv((p) => ({ ...p, elements: [...p.elements, el] }));
    setDirty(true);
    setSel(newId);
    setSideTab("elements");
    if (kind !== "route" && !presetName) {
      const road = await roadName(kind === "street_cut" || kind === "special_path" || kind === "reversed_oneway" ? coords[Math.floor(coords.length / 2)] : coords[0]);
      if (road) setEv((p) => ({ ...p, elements: p.elements.map((e) => (e.id === newId && e.name === el.name ? { ...e, name: kind === "sign" ? `${SIGNS[e.sign_type]?.label || "Objeto"} · ${road}` : kind === "no_parking" ? `Zona ${road}` : kind === "street_cut" ? `Tramo ${road}` : kind === "special_path" ? `Paso especial ${road}` : kind === "reversed_oneway" ? `Sentido invertido ${road}` : `Corte ${road}` } : e)) }));
    }
  };

  const onMapClick = (p) => {
    if (mode === "assign") { addPost(p); return; }
    if (mode === "cut_point") { addElement("cut_point", [p]); setMode(null); return; }
    if (mode === "measure") { setMeasure((m) => [...m, p]); return; }
    if (mode === "box") { setBox((b) => (b.length >= 2 ? [p] : [...b, p])); return; }
    if (mode === "place_group" && group) { placeGroup({ ...group, anchor: p }); return; }
    if (mode?.startsWith("sign:")) {
      const t = mode.slice(5);
      addElement("sign", [p], undefined, { sign_type: t, name: `${SIGNS[t].label} ${ev.elements.filter((e) => e.kind === "sign").length + 1}` });
      return;
    }
    setDraft((d) => [...d, p]);
  };
  const finishDraw = () => {
    if (mode === "sign_line") addElement("sign", draft, undefined, { sign_type: "valla_linea", spacing: 2.5, name: `Línea de vallas ${ev.elements.filter((e) => e.sign_type === "valla_linea").length + 1}` });
    else addElement(mode, draft);
    setDraft([]); setMode(null);
  };
  const cancelDraw = () => { setDraft([]); setMode(null); };
  const pickOneWay = (w) => {
    addElement("reversed_oneway", [...w.coords].reverse().map((p) => [+p[0].toFixed(6), +p[1].toFixed(6)]), w.name ? `Sentido invertido ${w.name}` : undefined);
    setMode(null);
    toast.success("Sentido invertido añadido · ajuste el tramo y su horario");
  };

  const save = async (silent) => {
    const body = { ...ev };
    delete body.plan; delete body.id;
    const r = await api.put(`/events/${id}`, body);
    setEv((p) => ({ ...r.data, plan: p.plan }));
    setDirty(false);
    if (!silent) toast.success("Evento guardado");
    return r.data;
  };

  const onSave = async () => { setBusy("save"); try { await save(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };

  const generate = async () => {
    setBusy("plan");
    try {
      if (dirty) await save(true);
      const r = await api.post(`/events/${id}/plan`);
      const fresh = await api.get(`/events/${id}`);
      setEv((p) => ({ ...p, plan: r.data, elements: fresh.data.elements, share_token: fresh.data.share_token, updated_at: fresh.data.updated_at }));
      setPlanTab("summary");
      const ac = r.data.auto_cuts;
      toast.success(`Plan de tráfico generado${ac?.added ? ` · ${ac.added} cortes previos propuestos para revisar` : ""}`);
      if (ac?.status === "no_disponible") toast.warning("No se pudo analizar la red viaria (OpenStreetMap no disponible): sin cortes previos automáticos");
      setTimeout(() => document.getElementById("plan-section")?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!ev) return <div className="p-10 font-mono text-xs text-slate-500" data-testid="editor-loading">CARGANDO EVENTO…</div>;

  const plan = ev.plan;
  const stale = plan && (dirty || ev.updated_at > plan.generated_at);
  const selected = ev.elements.find((e) => e.id === sel);
  const proposals = ev.elements.filter((e) => e.auto_generated && !e.locked);
  const dov = ev.detour_overrides || {};
  const shownDetours = (plan?.detours || []).filter((d) => ev.elements.some((e) => e.id === d.element_id))
    .map((d) => (dov[d.element_id] ? { ...d, coords: dov[d.element_id], manual: true, distance_m: Math.round(pathM(dov[d.element_id])), duration_min: +(pathM(dov[d.element_id]) / 416).toFixed(1), streets: [], entry_point: dov[d.element_id][0] } : d));
  const units = buildUnits(ev.shifts || []);
  const anchor = (toMin(ev.start_time) ?? 600) - 360;
  const assignments = ev.assignments || [];
  const allStaff = staffPointsFrom(units, assignments, anchor);
  const shownStaff = staffAt(allStaff, at);
  const tlist = plan?.tasks || [];
  const slotsAll = allStaff.flatMap((p) => p.slots);
  const trange = slotsAll.length ? [Math.floor(Math.min(...slotsAll.map((s) => s.start)) / 5) * 5, Math.ceil(Math.max(...slotsAll.map((s) => s.end)) / 5) * 5 - 5] : null;
  const assignUnit = units.find((u) => u.key === assignKey);
  const unitPostsSel = assignUnit ? unitPosts(assignUnit, assignments, anchor, units) : [];
  const addPost = (p) => {
    if (!assignUnit) return;
    const mine = assignments.filter((a) => isMine(a, assignUnit, units));
    const near = ev.elements.filter((e) => e.coords?.length && e.kind !== "route").map((e) => ({ e, d: Math.min(...e.coords.map((c) => Math.hypot((c[0] - p[0]) * 110540, (c[1] - p[1]) * 82000))) }))
      .filter((x) => x.d < 60).sort((a, b) => a.d - b.d)[0];
    const a = { id: crypto.randomUUID(), unit_key: assignUnit.key, unit_id: assignUnit.id, loc: p, time: mine.length ? "" : hmOf(assignUnit.shiftFrom), place: near ? near.e.name : "", notes: "" };
    update({ assignments: [...assignments, a] });
    setMode(null);
    toast.success(`Ubicación ${mine.length + 1} asignada a ${assignUnit.tip ? `TIP ${assignUnit.tip}` : assignUnit.id} · indique la hora y las órdenes`);
    if (!near) roadName(p).then((road) => road && setEv((x) => ({ ...x, assignments: (x.assignments || []).map((y) => (y.id === a.id && !y.place ? { ...y, place: road } : y)) })));
  };
  const selectEl = (id) => { setSel(id); setPick(null); };
  const pickDetour = (id) => { setSel(null); setPick({ type: "detour", id }); };
  const pickStaff = (key) => {
    setSel(null); setPick({ type: "staff", id: key });
    const a = assignments.find((x) => x.id === key);
    const o = a && units.find((u) => isMine(a, u, units));
    if (o) setAssignKey(o.key);
  };
  const pDetour = pick?.type === "detour" && shownDetours.find((d) => d.element_id === pick.id);
  const pStaff = pick?.type === "staff" && (shownStaff.find((p) => p.key === pick.id) || allStaff.find((p) => p.key === pick.id));
  const pickPanel = pDetour ? (
    <DetourPanel d={pDetour} edited={!!dov[pDetour.element_id]} canEdit={canEdit}
      onEdit={() => update({ detour_overrides: { ...dov, [pDetour.element_id]: simplify(pDetour.coords, 6).map((p) => [+p[0].toFixed(6), +p[1].toFixed(6)]) } })}
      onReset={() => { const n = { ...dov }; delete n[pDetour.element_id]; update({ detour_overrides: n }); }}
      onOpenCut={() => selectEl(pDetour.element_id)} />
  ) : pStaff ? (
    <StaffPanel p={pStaff} at={at} moved={false} canEdit={canEdit} onReset={() => {}}
      onOpenTask={(i) => { const a = assignments.find((x) => pStaff.task_ids.includes(x.id) && (x.place || "Puesto") === pStaff.tasks[i]); const o = a && units.find((u) => isMine(a, u, units)); if (o) { setAssignKey(o.key); document.querySelector("[data-testid=assign-panel]")?.scrollIntoView({ behavior: "smooth", block: "center" }); } }} />
  ) : null;
  const moveStaff = (key, loc) => {
    const p = allStaff.find((x) => x.key === key);
    if (!p) return;
    // with a unit selected, move only its post; otherwise the whole group standing there
    const own = assignUnit && p.task_ids.filter((t) => unitPostsSel.some((x) => x.id === t));
    const ids = own?.length ? own : p.task_ids;
    update({ assignments: assignments.map((a) => (ids.includes(a.id) ? { ...a, loc } : a)) });
  };
  const timings = plan?.timings || {};
  const deleteSelected = () => {
    if (!selected) return;
    update({ elements: ev.elements.filter((e) => e.id !== selected.id), ...(selected.auto_generated ? { auto_cuts_dismissed: [...(ev.auto_cuts_dismissed || []), selected.auto_origin || selected.coords[0]] } : {}) });
    setSel(null);
    toast.success(`«${selected.name || "Elemento"}» eliminado · pulse Guardar para confirmar`);
  };
  delRef.current = canEdit && !mode ? deleteSelected : null;
  const selDetour = selected && plan?.detours?.find((d) => d.element_id === selected.id);
  const elementForm = selected && (
    <>
    {selDetour && !selDetour.coords?.length && selDetour.note && (
      <div className="mb-3 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5" data-testid="element-detour-note">{selDetour.note}</div>
    )}
    {selDetour?.coords?.length > 0 && (
      <button onClick={() => pickDetour(selected.id)} className="mb-3 w-full text-left text-[11px] text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5 hover:bg-emerald-500/20 transition-colors" data-testid="element-detour-link">
        Desvío: {selDetour.distance_m} m · {selDetour.streets.slice(0, 4).join(" → ")}{selDetour.streets.length > 4 ? "…" : ""}
      </button>
    )}
    <ElementForm key={selected.id} el={selected} readOnly={!canEdit} timing={stale ? null : timings[selected.id]}
      onChange={(n) => setElements((els) => els.map((e) => (e.id === n.id ? n : e)))}
      onDelete={() => deleteSelected()}
      onSaveTemplate={canEdit ? () => saveTemplate(selected) : undefined} />
    </>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1700px]">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-sky-300 mb-2" data-testid="back-to-events-link"><ArrowLeft className="w-3.5 h-3.5" /> Archivo de eventos</Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="event-title">{ev.name}</h1>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS[ev.status]?.cls}`} data-testid="event-status-badge">{STATUS[ev.status]?.label}</span>
            {dirty && <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-300" data-testid="unsaved-badge">Sin guardar</span>}
          </div>
          <div className="font-mono text-xs text-slate-400 mt-1 flex items-center gap-2"><MapPin className="w-3 h-3" />{EVENT_TYPES[ev.type]} · {ev.date} · {ev.start_time}–{ev.end_time} · <b className="text-sky-300" data-testid="event-town-label">{ev.town?.name || "Sin población"}</b>{ev.location ? ` · ${ev.location}` : ""}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && <Button variant="outline" onClick={() => { setInfo({ ...ev }); setInfoOpen(true); }} data-testid="edit-event-info-button"><Pencil className="w-4 h-4 mr-1" /> Datos</Button>}
          {canEdit && <Button variant="outline" onClick={() => setDup(ev)} data-testid="duplicate-event-button"><Copy className="w-4 h-4 mr-1" /> Duplicar</Button>}
          {canEdit && <Button variant="outline" onClick={onSave} disabled={!dirty || !!busy} data-testid="save-event-button">{busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar</Button>}
          <Button variant="outline" onClick={() => nav(`/events/${id}/compare`)} data-testid="compare-editions-button"><GitCompare className="w-4 h-4 mr-1" /> Comparar ediciones</Button>
          {plan && <Button variant="outline" onClick={() => window.open(`/events/${id}/sheets`, "_blank")} data-testid="service-sheets-button"><FileText className="w-4 h-4 mr-1" /> Hojas de servicio</Button>}
          {plan && <Button variant="outline" onClick={() => window.open(`/events/${id}/print`, "_blank")} data-testid="export-pdf-plan-button"><FileDown className="w-4 h-4 mr-1" /> Exportar PDF</Button>}
          {canEdit && (
            <Button onClick={generate} disabled={!!busy} className="font-semibold shadow-[0_0_24px_rgba(14,165,233,0.35)]" data-testid="generate-traffic-plan-button">
              {busy === "plan" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />} {plan ? "Recalcular plan" : "Generar plan de tráfico"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7 xl:col-span-8 space-y-4">
        <div className={full ? "fixed inset-0 z-[60] bg-[#050a18] isolate" : "h-[620px] rounded-xl overflow-hidden border border-slate-700/60 shadow-2xl relative isolate z-0"} data-testid="event-map">
          {canEdit && <DrawToolbar mode={mode} setMode={(m) => { if (mode === "place_group") cancelGroup(); setMode(m); setDraft([]); setBox([]); if (m === "reversed_oneway") setOneWays(true); }} draft={draft} onFinish={finishDraw} onCancel={cancelDraw} onUndo={() => setDraft((d) => d.slice(0, -1))} />}
          <EventMap elements={ev.elements} detours={shownDetours} timings={timings} selectedId={sel} onSelect={selectEl}
            onSelectDetour={pickDetour} selectedDetour={pDetour ? pDetour.element_id : null}
            onDetourChange={canEdit ? (eid, coords) => update({ detour_overrides: { ...dov, [eid]: coords } }) : undefined}
            onSelectStaff={pickStaff} selectedStaff={pStaff ? pStaff.key : null} onMoveStaff={canEdit ? moveStaff : undefined}
            drawMode={mode} draft={draft} onMapClick={onMapClick} fitTrigger={fit}
            onElementChange={canEdit ? (n) => setElements((els) => els.map((e) => (e.id === n.id ? n : e))) : undefined}
            showOneWays={oneWays} onOneWayStatus={setOneWayStatus} onPickOneWay={pickOneWay} base={base} hidden={hidden} resizeKey={full} measure={measure} box={box} center={ev.town ? [ev.town.lat, ev.town.lng] : undefined} staff={shownStaff}
            posts={unitPostsSel} focus={focus} />
          {trange && !mode && (
            <div className="absolute bottom-[52px] left-1/2 -translate-x-1/2 z-[550]">
              <TimeBar range={trange} at={at} setAt={setAt} playing={playing} setPlaying={setPlaying} />
            </div>
          )}
          {ev.town?.name && (
            <div className="absolute bottom-[88px] right-3 z-[500] flex items-center gap-1.5 bg-[#090f20]/90 backdrop-blur-md border border-sky-500/30 rounded-lg px-3 py-1.5 text-xs text-sky-100" data-testid="map-town-badge">
              <MapPin className="w-3.5 h-3.5 text-sky-400" /><b>{ev.town.name}</b>{ev.town.province && <span className="text-slate-400">· {ev.town.province}</span>}
            </div>
          )}
          {mode === "measure" && <MeasurePanel pts={measure} onUndo={() => setMeasure((m) => m.slice(0, -1))} onClear={() => setMeasure([])} onClose={() => { setMeasure([]); setMode(null); }} />}
          {mode === "box" && <BoxSavePanel corners={box} count={box.length === 2 ? ev.elements.filter((e) => e.kind === "sign" && e.sign_type !== "valla_linea" && inBox(e.coords[0], box[0], box[1])).length : 0} onSave={saveBoxTemplate} onCancel={() => { setBox([]); setMode(null); }} />}
          {mode === "place_group" && group && <GroupPlacePanel tpl={group.tpl} placed={!!group.anchor} rot={group.rot} setRot={(rot) => placeGroup({ ...group, rot })}
            onDone={() => { toast.success(`${group.tpl.items.length} objeto(s) colocados`); setGroup(null); setMode(null); }} onCancel={cancelGroup} />}
          <div className={`absolute top-3 z-[500] flex flex-col items-end gap-1 ${full && (selected || pickPanel) ? "right-[376px]" : "right-3"}`}>
            <button onClick={() => setFull((v) => !v)} data-testid="map-fullscreen-button" title={full ? "Salir de pantalla completa (Esc)" : "Pantalla completa"}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border backdrop-blur-md transition-colors bg-[#090f20]/90 text-slate-300 border-white/10 hover:bg-white/10">
              {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />} {full ? "Salir" : "Pantalla completa"}
            </button>
            <MapLayersPanel base={base} setBase={setBase} hidden={hidden} setHidden={setHidden} />
            <MeasureButton active={mode === "measure"} onClick={() => { setMeasure([]); setDraft([]); if (mode === "place_group") cancelGroup(); setMode(mode === "measure" ? null : "measure"); }} />
            <button onClick={() => setOneWays((v) => !v)} data-testid="toggle-oneways-button"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border backdrop-blur-md transition-colors ${oneWays ? "bg-teal-500 text-slate-950 border-teal-400" : "bg-[#090f20]/90 text-slate-300 border-white/10 hover:bg-white/10"}`}>
              <ArrowUpRight className="w-4 h-4" /> Sentido único
            </button>
            {oneWays && oneWayStatus && <span className="text-[10px] bg-[#090f20]/90 text-teal-200 px-2 py-1 rounded" data-testid="oneways-status">{oneWayStatus}</span>}
          </div>
          {canEdit && selected && !mode && (
            <div className="absolute top-[100px] left-14 z-[500] bg-[#090f20]/95 border border-sky-500/30 rounded-lg pl-3 pr-1 py-1 text-[10px] text-sky-200 flex items-center gap-3" data-testid="vertex-edit-hint">
              <span><b className="text-white">{selected.name}</b> · {selected.kind === "cut_point" || (selected.kind === "sign" && selected.sign_type !== "valla_linea") ? "Arrastre el punto para moverlo" : "Arrastre los cuadrados blancos para mover · azules para añadir punto · clic derecho para borrar"}</span>
              <button onClick={deleteSelected} title="Eliminar (tecla Supr)" data-testid="map-delete-selected-button"
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/30 text-[11px] font-semibold transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Eliminar
              </button>
              <button onClick={() => setSel(null)} title="Cerrar" className="p-1 text-slate-400 hover:text-white" data-testid="map-deselect-button"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="absolute bottom-3 left-3 z-[500] bg-[#090f20]/90 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 flex flex-wrap gap-3 text-[10px] text-slate-300" data-testid="map-legend">
            {Object.entries(KINDS).filter(([k]) => k !== "sign").map(([k, v]) => <span key={k} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />{v.short}</span>)}
            <span className="flex items-center gap-1"><NoEntryMini />Corte previo</span>
            {Object.entries(STAFF_GROUPS).map(([k, v]) => <span key={k} className="flex items-center gap-1"><StaffMini group={k} size={11} />{v.label}</span>)}
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" />Desvío</span>
            {oneWays && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-teal-500" />Sentido único</span>}
          </div>
          {full && (selected || pickPanel) && (
            <div className="absolute top-3 right-3 bottom-20 w-[360px] z-[600] bg-[#111c38]/95 backdrop-blur-md border border-white/10 rounded-xl p-4 overflow-y-auto rise" data-testid="fullscreen-element-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] tracking-widest text-sky-400">ELEMENTO</span>
                <button onClick={() => { setSel(null); setPick(null); }} className="text-slate-400 hover:text-white" data-testid="fullscreen-panel-close"><X className="w-4 h-4" /></button>
              </div>
              {elementForm || pickPanel}
            </div>
          )}
          {mode === "assign" && assignUnit && (
            <div className="absolute bottom-[100px] left-1/2 -translate-x-1/2 z-[600] bg-amber-400 text-slate-950 text-xs font-semibold rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg" data-testid="assign-map-hint">
              Pulse en el mapa la ubicación de {assignUnit.tip ? `TIP ${assignUnit.tip}` : assignUnit.id}
              <button onClick={() => setMode(null)} className="underline">Cancelar</button>
            </div>
          )}
        </div>
        <AssignPanel units={units} assignments={assignments} anchor={anchor} selKey={assignKey} setSelKey={setAssignKey}
          armed={mode === "assign"} setArmed={(v) => { setDraft([]); setMode(v ? "assign" : null); }} canEdit={canEdit}
          onChange={(a) => update({ assignments: a })} onFocus={(loc) => setFocus({ loc, t: Date.now() })} />
        </div>

        <div className="lg:col-span-5 xl:col-span-4 bg-[#111c38] border border-white/10 rounded-xl p-5">
          <Tabs value={sideTab} onValueChange={setSideTab}>
            <TabsList className="w-full bg-[#0b1329]">
              <TabsTrigger value="elements" className="flex-1" data-testid="tab-elements">Elementos ({ev.elements.length})</TabsTrigger>
              <TabsTrigger value="templates" className="flex-1" data-testid="tab-templates">Plantillas</TabsTrigger>
              <TabsTrigger value="briefing" className="flex-1" data-testid="tab-briefing">Briefing</TabsTrigger>
            </TabsList>
            <TabsContent value="templates" className="mt-4">
              <TemplatesPanel canEdit={canEdit} elements={ev.elements} onAdd={addFromTemplate} />
            </TabsContent>
            <TabsContent value="elements" className="space-y-4 mt-4">
              <label className="flex items-center justify-between gap-3 bg-[#0b1329] border border-white/5 rounded-lg px-3 py-2 cursor-pointer" data-testid="auto-cuts-toggle-row">
                <span><span className="text-xs text-slate-200 block">Cortes previos automáticos</span>
                  <span className="text-[10px] text-slate-500">Al generar el plan, valla el último cruce antes de cada calle afectada</span></span>
                <Switch checked={ev.auto_cuts !== false} disabled={!canEdit} onCheckedChange={(v) => update({ auto_cuts: v })} data-testid="auto-cuts-switch" />
              </label>
              {proposals.length > 0 && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2.5 space-y-2" data-testid="auto-cuts-review">
                  <div className="text-xs text-rose-100"><b>{proposals.length}</b> cortes previos propuestos. Revíselos en el mapa: solo cuentan en el plan cuando los acepta.</div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button size="sm" className="h-7 bg-emerald-500 hover:bg-emerald-400 text-slate-950" onClick={() => setElements((els) => els.map((e) => (e.auto_generated && !e.locked ? { ...e, locked: true } : e)))} data-testid="auto-cuts-accept-all-button">Aceptar todos</Button>
                      <Button size="sm" variant="outline" className="h-7 text-red-300" onClick={() => { update({ elements: ev.elements.filter((e) => !(e.auto_generated && !e.locked)), auto_cuts_dismissed: [...(ev.auto_cuts_dismissed || []), ...proposals.map((e) => e.auto_origin || e.coords[0])] }); setSel(null); }} data-testid="auto-cuts-reject-all-button">Rechazar todos</Button>
                    </div>
                  )}
                </div>
              )}
              <ElementList elements={ev.elements} selectedId={sel} onSelect={selectEl} timings={stale ? null : timings} />
              {selected || pickPanel ? (
                <div className="border-t border-white/10 pt-4">
                  {!full && (elementForm || pickPanel)}
                </div>
              ) : ev.elements.length > 0 && <div className="text-xs text-slate-500">Seleccione un elemento para editar horarios y personal.</div>}
            </TabsContent>
            <TabsContent value="briefing" className="mt-4">
              <ShiftsBriefing eventId={id} shifts={ev.shifts} isNew={briefingNew} readOnly={!canEdit} summary={stale ? null : plan?.summary}
                onChange={(s) => { setBriefingNew(false); update({ shifts: s, resources: totalsFromShifts(s) }); }} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <section id="plan-section" className="mt-8 bg-slate-900/80 border border-slate-800 rounded-xl p-6 backdrop-blur-md" data-testid="plan-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="font-mono text-xs tracking-[0.25em] text-sky-400">PLAN DE TRÁFICO</div>
            <h2 className="font-display text-2xl font-bold">Propuesta operativa</h2>
          </div>
          {plan && <div className="font-mono text-[11px] text-slate-500">Generado {new Date(plan.generated_at).toLocaleString("es-ES")}</div>}
        </div>
        {stale && <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2 mb-4" data-testid="plan-stale-warning"><AlertTriangle className="w-4 h-4" /> Hay cambios desde el último cálculo. Pulse “Recalcular plan”.</div>}
        {!plan ? (
          <div className="text-sm text-slate-400 border border-dashed border-white/10 rounded-xl p-10 text-center" data-testid="plan-empty">
            Marque en el mapa el recorrido, los puntos de corte, tramos y zonas sin estacionamiento, indique el personal disponible y pulse <b className="text-sky-300">Generar plan de tráfico</b>.
          </div>
        ) : (
          <Tabs value={planTab} onValueChange={setPlanTab}>
            <TabsList className="bg-[#0b1329] flex-wrap h-auto">
              <TabsTrigger value="summary" data-testid="plan-tab-summary">Resumen</TabsTrigger>
              <TabsTrigger value="detours" data-testid="plan-tab-detours">Desvíos ({plan.detours.length})</TabsTrigger>
              <TabsTrigger value="staff" data-testid="plan-tab-staff">Cronograma de personal</TabsTrigger>
              <TabsTrigger value="signage" data-testid="plan-tab-signage">Señalización y estacionamiento</TabsTrigger>
              <TabsTrigger value="report" data-testid="plan-tab-report">Orden de servicio (IA)</TabsTrigger>
            </TabsList>
            <TabsContent value="summary" className="mt-5"><PlanSummary plan={plan} elements={ev.elements} /></TabsContent>
            <TabsContent value="detours" className="mt-5"><DetoursList detours={plan.detours} /></TabsContent>
            <TabsContent value="staff" className="mt-5"><StaffGantt units={plan.units} eventId={id} shareToken={ev.share_token} /></TabsContent>
            <TabsContent value="signage" className="mt-5"><SignageTable signage={plan.signage} parking={plan.parking} /></TabsContent>
            <TabsContent value="report" className="mt-5">
              <AiReport eventId={id} text={plan.ai_report} canEdit={canEdit} stale={stale} onDone={(t) => setEv((p) => ({ ...p, plan: { ...p.plan, ai_report: t } }))} />
            </TabsContent>
          </Tabs>
        )}
      </section>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="bg-[#111c38] border-white/10 max-w-lg" data-testid="edit-event-dialog">
          <DialogHeader><DialogTitle className="font-display">Datos del evento</DialogTitle></DialogHeader>
          {info && <EventInfoFields value={info} onChange={setInfo} withStatus />}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInfoOpen(false)}>Cancelar</Button>
            <Button data-testid="edit-event-apply-button" onClick={() => { const { name, type, date, start_time, end_time, status, location, description, town } = info; update({ name, type, date, start_time, end_time, status, location, description, town }); setInfoOpen(false); }}>Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicateDialog source={dup} onClose={() => setDup(null)} onDone={(n) => nav(`/events/${n.id}`)} />
    </div>
  );
}
